'use client';

import type { FC } from 'react';
import { useState } from 'react';

import { getAllTestParticipations } from '@packages/shared/src/abtesting/useAbTesting/getAllTestParticipations/getAllTestParticipations';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { setTestCookies } from '@packages/shared/src/abtesting/useAbTesting/utilities.cookie';

const isSimpleTest = (test: {
  testId: string;
  participantId: string;
  variant?: Record<string, string>;
}): test is {
  testId: string;
  participantId: string;
  variant: { treatment: 'test' | 'control' };
} => {
  if (!test.variant) return false;
  const { treatment } = test.variant;

  return (
    Object.keys(test.variant).length === 1 && (treatment === 'test' || treatment === 'control')
  );
};

export const TestOverview: FC = () => {
  const { getCookies, setCookie } = useCookies();

  const [activeTests, setActiveTests] = useState(getAllTestParticipations(getCookies()));

  const switchTestGroup = (test: {
    testId: string;
    participantId: string;
    variant: Record<string, string>;
  }) => {
    const newVariant = test.variant.treatment === 'test' ? 'control' : 'test';
    const updatedTest = {
      ...test,
      variant: {
        ...test.variant,
        treatment: newVariant,
      },
    };

    const value = {
      participant: test.participantId,
      variant: updatedTest.variant,
    };

    setTestCookies({ testId: test.testId, value, setCookie });
    setActiveTests(getAllTestParticipations(getCookies()));
  };

  return (
    <ul>
      {activeTests.map((test) => (
        <li
          key={test.testId}
          className="border-color-grey flex items-center gap-1 rounded-sm border p-2 shadow-xs"
        >
          <div className="flex flex-1 flex-col items-start gap-0.5">
            <span className="text-size-body1">{test.testId}</span>
            <code className="bg-color-grey-light rounded-sm p-0.5">
              <pre className="text-color-grey-dark">{JSON.stringify(test.variant)}</pre>
            </code>
          </div>
          {isSimpleTest(test) ? (
            <Button onClick={() => switchTestGroup(test)}>Switch test group</Button>
          ) : (
            <Button disabled title="Test has complex variants, switching is not supported yet">
              Switch test group
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
};
